import produce from 'immer';

// @ts-strict-ignore
import {isDateSegment, isUserSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {CohortType, Retention} from 'common/report/retention/models/types';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {
  ReportBasedCohorts,
  SerializedAddictionReport,
  SerializedAddictionReportBehavior,
  SerializedAddictionReportBehaviorParam,
  SerializedAddictionReportWrapper,
  SerializedRetentionReportBehavior,
  SerializedRetentionReportBehaviorParam,
  SerializedRetentionReportWrapper,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {Expression} from 'common/query-builder/cohorts-query-builder/models/types/expression-types';
import {ArithmeticOperator, Property} from 'common/query-builder/cohorts-query-builder/models/enums/expression-enums';
import {getCalendarDate, parseDatetimeRange, parseSpecialDate} from 'common/util/date';
import {Report} from 'common/report/insights/models/report';
import {InsightsBookmarkParams, SpecialTimeUnit} from 'common/types/reports/bookmark';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';
import {InsightsQueryResults} from 'common/report/insights/util/types';

const getBehavior = ({isAddictionReport, isAverageBehavior, rowIdx, columnIdx}) => {
  if (isAddictionReport) {
    const behavior: SerializedAddictionReportBehavior = {
      addiction_index: columnIdx + 1,
    };
    if (!isAverageBehavior) {
      behavior.addiction_interval_index = rowIdx;
    }
    return behavior;
  }

  const behavior: SerializedRetentionReportBehavior = {
    retained_interval_index: columnIdx,
  };
  if (!isAverageBehavior) {
    behavior.birth_interval_index = rowIdx;
  }
  return behavior;
};

const createSegments = ({segments, groupBy}: {segments: string[]; groupBy: Segment[]}) => {
  const modifiedSegments = [];
  groupBy.forEach((group, index) => {
    let segmentValue = segments[index];
    if (isDateSegment(group)) {
      const timeUnit = isUserSegment(group) ? group.user.unit : group.event.unit;
      if (timeUnit === SpecialTimeUnit.HourOfDay || timeUnit === SpecialTimeUnit.DayOfWeek) {
        segmentValue = parseSpecialDate(segmentValue, timeUnit);
      } else {
        const dateRange = parseDatetimeRange(segmentValue, timeUnit, 1);
        const dateTime = Array.isArray(dateRange) ? dateRange[0] : dateRange;
        segmentValue = getCalendarDate(dateTime);
      }
    }
    modifiedSegments.push(segmentValue);
  });

  return modifiedSegments;
};

const getBehaviorParams = ({
  isAddictionReport,
  behaviorType,
  behavior,
  segments,
  groupBy,
}: {
  isAddictionReport: boolean;
  behaviorType: CohortType;
  behavior: SerializedRetentionReportBehavior | SerializedAddictionReportBehavior;
  segments: string[];
  groupBy: Segment[];
}) => {
  const modifiedSegments = createSegments({segments, groupBy});
  const behaviorParamsKey = isAddictionReport ? `addicted_behavior` : `retained_behavior`;
  let behaviorParams;
  switch (behaviorType) {
    case CohortType.SpecificIntervalRetained: {
      behaviorParams = [
        {
          [behaviorParamsKey]: behavior,
          segments: modifiedSegments,
        },
      ];
      break;
    }
    case CohortType.SpecificIntervalDropOff:
      behaviorParams = [
        {
          drop_off_behavior: behavior,
          segments: modifiedSegments,
        },
      ];
      break;
  }
  if (isAddictionReport) {
    return behaviorParams as SerializedAddictionReportBehaviorParam[];
  }
  return behaviorParams as SerializedRetentionReportBehaviorParam[];
};

const getReportBookmark = (
  bookmark: InsightsBookmarkParams,
  queryResults: InsightsQueryResults<Retention>,
  matchingShowClauseIdx: number,
) => {
  return produce(bookmark, (draft) => {
    draft.sections.show = [bookmark.sections.show[matchingShowClauseIdx]];
    draft.sections.group = bookmark.sections.group.map((group, index) => {
      const metaGroupClause = queryResults.meta?.report_sections?.group?.[index];
      if (metaGroupClause?.numeric_buckets) {
        group.customBucket = metaGroupClause.numeric_buckets;
      }
      return group;
    });
    return draft;
  });
};

const isDisableCohortize = (bookmark: InsightsBookmarkParams, matchingShowClauseIdx: number) => {
  const showClause = bookmark.sections.show[matchingShowClauseIdx] as BehaviorShowClause;

  if (showClause) {
    return showClause.behavior.disableCohortize;
  }
  return false;
};

const getReportWrapper = ({
  reportType,
  segments,
  bookmark,
  queryResults,
  matchingShowClauseIdx,
  groupedRowIdx,
  columnIdx,
  behaviorType,
}: {
  reportType: string;
  segments: string[];
  bookmark: InsightsBookmarkParams;
  queryResults: InsightsQueryResults<Retention>;
  matchingShowClauseIdx: number;
  groupedRowIdx: number;
  columnIdx: number;
  behaviorType: CohortType;
}) => {
  const rowIdx = Number.isFinite(groupedRowIdx) ? groupedRowIdx : 0;
  const isAddictionReport = reportType === `addiction-cumulative` || reportType === `addiction-non-cumulative`;
  const retentionCumulative = reportType === `addiction-cumulative`;
  const groupBy = getSelectedSegments(Report.fromBookmarkData(bookmark).sections.group.clauses);

  // If cohortizing is disabled, there should be just a single interval for the report, so we should just use the standard behavior params.
  const disableCohortize = isDisableCohortize(bookmark, matchingShowClauseIdx);
  const isAverageBehavior = !Number.isFinite(groupedRowIdx) && !disableCohortize;

  const behavior = getBehavior({isAddictionReport, isAverageBehavior, rowIdx, columnIdx});
  const behaviorParams = getBehaviorParams({isAddictionReport, behaviorType, behavior, segments, groupBy});

  const report = {
    bookmark: getReportBookmark(bookmark, queryResults, matchingShowClauseIdx),
    ...(isAverageBehavior
      ? // Creating a cohort from the average row will include all other rows. `average_behavior_params` will
        // be resolved to a list of `behavior_params` at query time.
        {average_behavior_params: behaviorParams}
      : {behavior_params: behaviorParams}),
  };

  if (isAddictionReport) {
    (report as SerializedAddictionReport).cumulative = retentionCumulative;
    return {
      [ReportBasedCohorts.Addiction]: report,
    } as SerializedAddictionReportWrapper;
  }

  return {
    [ReportBasedCohorts.Retention]: report,
  } as SerializedRetentionReportWrapper;
};

const selector: Expression = {
  operator: ArithmeticOperator.Equals,
  children: [
    {
      property: Property.Behaviors,
      value: `A0`,
    },
    {
      property: Property.Literal,
      value: true,
    },
  ],
};

export function createCohort({
  name,
  description,
  isSegmented,
  bookmark,
  queryResults,
  matchingShowClauseIdx,
  groupedRowIdx,
  columnIdx,
  segments,
  behaviorType,
  reportType,
}: {
  name: string;
  description: string;
  isSegmented: boolean;
  bookmark: InsightsBookmarkParams;
  queryResults: InsightsQueryResults<Retention>;
  matchingShowClauseIdx: number;
  groupedRowIdx: number;
  columnIdx: number;
  segments: string[];
  behaviorType: CohortType;
  reportType: string;
}): Cohort {
  const behaviors = {
    A0: getReportWrapper({
      reportType,
      segments: isSegmented ? segments : [],
      bookmark,
      queryResults,
      matchingShowClauseIdx,
      groupedRowIdx,
      columnIdx,
      behaviorType,
    }),
  };
  const dataGroupId = bookmark.sections.globalDataGroupId;
  return Cohort.parseCohort({
    behaviors,
    name,
    description,
    selector,
    ...(dataGroupId ? {data_group_id: dataGroupId} : {}),
  });
}
